import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Svg, { Circle, Line, Rect } from 'react-native-svg';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import type { MatchShot, TeamRef } from '@/lib/data/provider';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

/** A shot carries goal-mouth coords only when both gm_y and gm_z are present. */
export function hasGoalMouth(shot: MatchShot): boolean {
  return typeof shot.gm_y === 'number' && typeof shot.gm_z === 'number';
}

/** True when ANY shot in the set has plottable goal-mouth coords (drives the
 *  Pitch/Goal toggle's visibility). */
export function anyGoalMouth(shots: MatchShot[]): boolean {
  return shots.some(hasGoalMouth);
}

// Goal-frame geometry, in the SVG's own viewBox units. The frame is drawn inside
// a padded area so dots near the posts/crossbar never clip the card edge.
const VIEW_W = 320;
const VIEW_H = 150;
const PAD_X = 26;
const PAD_TOP = 22;
const FRAME_W = VIEW_W - PAD_X * 2; // post-to-post span on screen
const FRAME_H = VIEW_H - PAD_TOP - 18; // crossbar-to-ground span on screen

// Provider goal-mouth ranges we stretch across the drawn frame. gm_y is the goal
// WIDTH (centre 50, posts ~44/56); we map a slightly wider [42,58] so on-target
// shots that flirt with the posts still land inside the frame. gm_z is HEIGHT
// (0 = ground, crossbar ~40); we map [0,42] and INVERT so 0 sits at the bottom.
const Y_MIN = 42;
const Y_MAX = 58;
const Z_MIN = 0;
const Z_MAX = 42;

// Fallback frame (no gm coords): the shot's pitch-width `y` (0–100) is laid
// across a slightly inset goal width so on-target shots spread inside the posts;
// off-target shots are pushed JUST outside the frame so the on/off read survives.
const FALLBACK_INSET = 0.16; // keep on-target dots off the very posts
const FALLBACK_Z = 0.42; // mid-low default height (fraction up the frame)
const OFF_PUSH = 0.13; // how far outside the frame an off-target dot sits

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Map one shot's gm coords to {cx, cy} in viewBox units (left post = low gm_y,
 *  ground = gm_z 0 at the bottom). */
function plot(shot: MatchShot): { cx: number; cy: number } {
  const y = clamp(shot.gm_y ?? 50, Y_MIN, Y_MAX);
  const z = clamp(shot.gm_z ?? 0, Z_MIN, Z_MAX);
  const cx = PAD_X + ((y - Y_MIN) / (Y_MAX - Y_MIN)) * FRAME_W;
  // Inverted: gm_z 0 → bottom (PAD_TOP + FRAME_H), gm_z max → top (PAD_TOP).
  const cy = PAD_TOP + FRAME_H - ((z - Z_MIN) / (Z_MAX - Z_MIN)) * FRAME_H;
  return { cx, cy };
}

/**
 * Fallback plot for a shot WITHOUT goal-mouth coords: lay it across the goal
 * width by its pitch-width `y` (0–100). On-target / goal shots land INSIDE the
 * frame (slightly inset off the posts) at a sensible mid-height; off-target shots
 * are nudged just OUTSIDE the nearer post so the in-frame vs wide read survives.
 */
function plotFallback(shot: MatchShot): { cx: number; cy: number } {
  const onFrame = shot.is_on_target || shot.is_goal;
  // y → 0…1 across the goal width, compressed inside the posts for on-frame shots.
  const yf = clamp(shot.y ?? 50, 0, 100) / 100;
  const inset = onFrame ? FALLBACK_INSET : 0;
  const frac = inset + yf * (1 - inset * 2);
  let cx = PAD_X + frac * FRAME_W;
  if (!onFrame) {
    // Push wide shots just outside the nearer post (left half → left, else right).
    cx += frac < 0.5 ? -OFF_PUSH * FRAME_W : OFF_PUSH * FRAME_W;
  }
  const cy = PAD_TOP + FRAME_H - FALLBACK_Z * FRAME_H;
  return { cx, cy };
}

/** A single legend swatch + label (mirrors the pitch shotmap legend). */
function LegendItem({
  fill,
  border,
  label,
  opacity = 1,
}: {
  fill: string;
  border: string;
  label: string;
  opacity?: number;
}) {
  return (
    <View className="flex-row items-center gap-1.5" style={{ opacity }}>
      <View
        style={{
          width: 11,
          height: 11,
          borderRadius: 6,
          backgroundColor: fill,
          borderWidth: 1.5,
          borderColor: border,
        }}
      />
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

/**
 * A front-on GOAL FRAME (two posts + crossbar + a hint of net) plotting where
 * each on-frame shot crossed the goal line. When the provider supplies goal-mouth
 * coords we use them for precise placement; when it omits them entirely we FALL
 * BACK to laying every on-target / goal shot across the goal width by its pitch
 * `y` (off-target shots just outside the posts), so the Goal view always shows
 * something rather than an empty frame. Goals are solid accent, on-target-but-
 * saved shots hollow. Rendered in one react-native-svg node (already a
 * dependency) and laid out LTR so the goal reads the same way in both directions.
 */
export function GoalMouth({
  shots,
  homeTeam,
  awayTeam,
}: {
  shots: MatchShot[];
  homeTeam?: TeamRef | null;
  awayTeam?: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  // Whether ANY shot carries true goal-mouth coords. When some do, we plot ONLY
  // those (the precise placement); when none do, we fall back to laying every
  // on-target / goal shot across the goal width by `y` so the Goal view still
  // shows something instead of an empty frame.
  const hasPreciseCoords = useMemo(() => shots.some(hasGoalMouth), [shots]);

  // The drawn set, goals last so they sit on top. Precise mode keeps only shots
  // with gm coords; fallback mode keeps every on-target / goal shot.
  const plotted = useMemo(
    () =>
      shots
        .filter((shot) =>
          hasPreciseCoords ? hasGoalMouth(shot) : shot.is_on_target || shot.is_goal,
        )
        .slice()
        .sort((a, b) => Number(a.is_goal) - Number(b.is_goal)),
    [shots, hasPreciseCoords],
  );

  const height = width > 0 ? (width * VIEW_H) / VIEW_W : VIEW_H;
  const netColor = palette.pitchLine;

  return (
    <View>
      <View
        className="bg-pitch"
        style={{ height, direction: 'ltr' }}
        onLayout={onLayout}
      >
        {width > 0 ? (
          <Svg width={width} height={height} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}>
            {/* Net hint: a light grid between the posts, under the frame. */}
            {[0.25, 0.5, 0.75].map((f) => (
              <Line
                key={`v-${f}`}
                x1={PAD_X + FRAME_W * f}
                y1={PAD_TOP}
                x2={PAD_X + FRAME_W * f}
                y2={PAD_TOP + FRAME_H}
                stroke={netColor}
                strokeWidth={0.75}
                opacity={0.18}
              />
            ))}
            {[0.33, 0.66].map((f) => (
              <Line
                key={`h-${f}`}
                x1={PAD_X}
                y1={PAD_TOP + FRAME_H * f}
                x2={PAD_X + FRAME_W}
                y2={PAD_TOP + FRAME_H * f}
                stroke={netColor}
                strokeWidth={0.75}
                opacity={0.18}
              />
            ))}
            {/* Ground line under the goal. */}
            <Line
              x1={PAD_X - 12}
              y1={PAD_TOP + FRAME_H}
              x2={PAD_X + FRAME_W + 12}
              y2={PAD_TOP + FRAME_H}
              stroke={netColor}
              strokeWidth={2}
              opacity={0.5}
            />
            {/* The frame: left post, right post, crossbar (crisp white). */}
            <Rect
              x={PAD_X}
              y={PAD_TOP}
              width={FRAME_W}
              height={FRAME_H}
              fill="none"
              stroke={netColor}
              strokeWidth={3}
              opacity={0.9}
            />
            {/* Shot dots — home = volt, away = live (dimmed to stay apart, as on
                the pitch shotmap). Goals are solid + a bright ring; saved = hollow. */}
            {plotted.map((shot, index) => {
              const { cx, cy } = hasPreciseCoords ? plot(shot) : plotFallback(shot);
              const isHome = shot.team_side === 'home';
              const teamColor = isHome ? palette.volt : palette.teamAway;
              const fill = shot.is_goal ? teamColor : 'transparent';
              const border = shot.is_goal ? palette.voltBright : teamColor;
              return (
                <Circle
                  key={`${shot.team_side}-${shot.minute}-${shot.player_name}-${index}`}
                  cx={cx}
                  cy={cy}
                  r={shot.is_goal ? 6 : 5}
                  fill={fill}
                  stroke={border}
                  strokeWidth={shot.is_goal ? 2 : 1.5}
                  opacity={isHome ? 1 : 0.62}
                />
              );
            })}
          </Svg>
        ) : null}
        {plotted.length === 0 ? (
          <View className="absolute inset-0 items-center justify-center">
            <AppText variant="micro" className="text-pitch-line">
              {t('match.noGoalPlacement')}
            </AppText>
          </View>
        ) : null}
      </View>
      {/* Legend: goal vs on-target (saved), plus the home/away colour key. */}
      <View className="gap-1.5 px-4 py-2.5">
        <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1.5">
          <LegendItem fill={palette.volt} border={palette.voltBright} label={t('match.goal')} />
          <LegendItem fill="transparent" border={palette.voltLine} label={t('match.onTarget')} />
        </View>
        {homeTeam || awayTeam ? (
          <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1.5">
            <LegendItem
              fill={palette.volt}
              border={palette.voltBright}
              label={localizeTeamName(homeTeam ?? null, locale, true)}
            />
            <LegendItem
              fill={palette.teamAway}
              border={palette.voltBright}
              label={localizeTeamName(awayTeam ?? null, locale, true)}
              opacity={0.62}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
}
